import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, BooleanDecoder, DateDecoder, field, IntegerDecoder, StringDecoder } from "@simonbackx/simple-encoding";
import { Formatter, StringCompare } from "@stamhoofd/utility";
import { v4 as uuidv4 } from "uuid";
import { Address } from "../addresses/Address";
import { File } from "../files/File";
import { Payment, Settlement } from "../members/Payment";
import { OrganizationSimple } from "../OrganizationSimple";
import { STPackage, STPricingType } from "./STPackage";
export var STInvoiceStatus;
(function (STInvoiceStatus) {
    STInvoiceStatus["Created"] = "Created";
    STInvoiceStatus["Prepared"] = "Prepared";
    STInvoiceStatus["Completed"] = "Completed";
    STInvoiceStatus["Canceled"] = "Canceled";
})(STInvoiceStatus || (STInvoiceStatus = {}));
export class STInvoiceItem extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.name = "";
        this.description = "";
        this.amount = 1;
        this.unitPrice = 0;
        this.canUseCredits = true;
        this.firstFailedPayment = null;
        this.paymentFailedCount = 0;
    }
    get price() {
        return this.unitPrice * this.amount;
    }
    /**
     * Convertable into STInvoiceItem (or the diffence if amount is increased)
     * Use this to calculate prices or create an invoice
     * This will calculate the price to expand the package to the given amount.
     * If you want to renew a package, you need to create a new package first
     */
    static fromPackage(pack, amount = 1, pendingAmount = 0, date) {
        let unitPrice = Math.round(pack.meta.unitPrice);
        if (amount < pack.meta.minimumAmount) {
            // Minimum should get applied first, because we might already have paid for the minimum (paid amount)
            amount = pack.meta.minimumAmount;
        }
        amount -= pendingAmount;
        amount -= pack.meta.paidAmount;
        if (amount <= 0) {
            amount = 0;
        }
        /// When pricing type is memebrs, the price is calculated per year.
        /// If a shorter period is remaining, we give a discount in order
        /// to no need to handle it more complicated
        let now = date !== null && date !== void 0 ? date : new Date();
        if (now < pack.meta.startDate) {
            // When creating a new package, we sometimes buy it for the future, so use that date instead of now
            now = pack.meta.startDate;
        }
        if (pack.validUntil && pack.meta.pricingType !== STPricingType.Fixed) {
            const totalDays = Math.round((pack.validUntil.getTime() - pack.meta.startDate.getTime()) / (1000 * 60 * 60 * 24));
            let remainingDays = Math.round((pack.validUntil.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
            /// First 3 months are full price
            const paidDays = 30 * 3;
            if (remainingDays > totalDays) {
                remainingDays = totalDays;
            }
            if (totalDays > 366) {
                // Increase unit price
                unitPrice = unitPrice * (totalDays / 365);
            }
            if (pack.meta.pricingType === STPricingType.PerMember) {
                unitPrice = Math.round(Math.min(unitPrice, unitPrice * remainingDays / (Math.max(365, totalDays) - paidDays)));
            }
            else {
                unitPrice = Math.round(unitPrice);
            }
        }
        const item = STInvoiceItem.create({
            name: pack.meta.name,
            description: pack.validUntil ? ("Van " + Formatter.date(now, true) + " tot " + Formatter.date(pack.validUntil, true)) : ("Vanaf " + Formatter.date(pack.meta.startDate, true)),
            package: pack,
            date: now,
            unitPrice: unitPrice,
            amount: amount
        });
        return item;
    }
    canMerge(other) {
        // Mergeable if both don't have a packege, or both have the same package
        if (other.package && !this.package) {
            return false;
        }
        if (!other.package && this.package) {
            return false;
        }
        if (other.package && this.package && other.package.id !== this.package.id) {
            return false;
        }
        if (this.name === other.name && this.description === other.description) {
            if (this.unitPrice === other.unitPrice) {
                return true;
            }
            if (this.amount === 1 && other.amount === 1) {
                return true;
            }
        }
        return false;
    }
    merge(other) {
        if (other.paymentFailedCount > this.paymentFailedCount) {
            this.paymentFailedCount = other.paymentFailedCount;
        }
        if (other.firstFailedPayment && (!this.firstFailedPayment || other.firstFailedPayment < this.firstFailedPayment)) {
            this.firstFailedPayment = other.firstFailedPayment;
        }
        if (other.unitPrice !== this.unitPrice) {
            if (other.amount === 1 && this.amount === 1) {
                this.unitPrice += other.unitPrice;
                this.package = other.package;
                return;
            }
            throw new Error("Cannot merge items with different unit prices and amount greater than 1");
        }
        this.amount += other.amount;
    }
    /// Only compress an invoice when it is marked as paid and for a pending invoice when it doesn't has an invoice
    /// Else you'll lose the ID's!
    static compress(items) {
        const copy = items.slice();
        for (let index = 0; index < copy.length; index++) {
            // Create a copy to prevent changing the original one
            const item = STInvoiceItem.create(copy[index]);
            copy[index] = item;
            // Loop further (in reverse order to be able to delete items)
            for (let j = copy.length - 1; j > index; j--) {
                const other = copy[j];
                if (item.canMerge(other)) {
                    // Delete other
                    item.merge(other);
                    copy.splice(j, 1);
                }
            }
        }
        return copy;
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], STInvoiceItem.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], STInvoiceItem.prototype, "name", void 0);
__decorate([
    field({ decoder: StringDecoder })
], STInvoiceItem.prototype, "description", void 0);
__decorate([
    field({ decoder: IntegerDecoder })
], STInvoiceItem.prototype, "amount", void 0);
__decorate([
    field({ decoder: IntegerDecoder })
], STInvoiceItem.prototype, "unitPrice", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 155 })
], STInvoiceItem.prototype, "canUseCredits", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true, optional: true })
], STInvoiceItem.prototype, "firstFailedPayment", void 0);
__decorate([
    field({ decoder: IntegerDecoder, optional: true })
], STInvoiceItem.prototype, "paymentFailedCount", void 0);
__decorate([
    field({ decoder: STPackage, optional: true })
], STInvoiceItem.prototype, "package", void 0);
__decorate([
    field({ decoder: DateDecoder, optional: true })
], STInvoiceItem.prototype, "date", void 0);
export class STInvoiceMeta extends AutoEncoder {
    constructor() {
        super(...arguments);
        /**
         * VATPercentage should be zero in countries outside Belgium in EU
         */
        this.VATPercentage = 21;
        this.items = [];
        /**
         * Sometimes we need to calculate an invoice in reverse when we have a fixed price including VAT,
         * but need to calculatle the price excluding VAT.
         */
        this.areItemsIncludingVAT = false;
        this.companyVATNumber = null;
        this.companyNumber = null;
        this.ipAddress = null;
        this.userAgent = null;
        this.stripeAccountId = null;
    }
    /**
     * Depending on areItemsIncludingVAT, this can either be including or excluding VAT
     */
    get itemPrice() {
        return this.items.reduce((price, item) => price + item.price, 0);
    }
    includingVATToExcludingVAT(price) {
        // Always only round the VAT, not other prices
        return price - this.getVATOnIncludingVATAmount(price);
    }
    excludingVATToIncludingVAT(price) {
        // Always only round the VAT, not other prices
        return price + this.getVATOnExcludingVATAmount(price);
    }
    getVATOnIncludingVATAmount(price) {
        return Math.round(price * this.VATPercentage / (100 + this.VATPercentage));
    }
    getVATOnExcludingVATAmount(price) {
        // Make sure price result doesn't depend on the sign of the price
        return Math.round(Math.abs(price) * this.VATPercentage / 100) * Math.sign(price);
    }
    get priceWithoutVAT() {
        const itemPrice = this.itemPrice;
        if (this.areItemsIncludingVAT) {
            return itemPrice - this.VAT;
        }
        return itemPrice;
    }
    get VAT() {
        if (this.areItemsIncludingVAT) {
            // Subtract VAT and round
            return this.getVATOnIncludingVATAmount(this.itemPrice);
        }
        return this.getVATOnExcludingVATAmount(this.itemPrice);
    }
    get priceWithVAT() {
        const itemPrice = this.itemPrice;
        if (this.areItemsIncludingVAT) {
            return itemPrice;
        }
        return itemPrice + this.VAT;
    }
}
__decorate([
    field({ decoder: DateDecoder, optional: true })
], STInvoiceMeta.prototype, "date", void 0);
__decorate([
    field({ decoder: File, optional: true })
], STInvoiceMeta.prototype, "pdf", void 0);
__decorate([
    field({ decoder: IntegerDecoder })
], STInvoiceMeta.prototype, "VATPercentage", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(STInvoiceItem) })
], STInvoiceMeta.prototype, "items", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 186 })
], STInvoiceMeta.prototype, "areItemsIncludingVAT", void 0);
__decorate([
    field({ decoder: StringDecoder })
], STInvoiceMeta.prototype, "companyName", void 0);
__decorate([
    field({ decoder: StringDecoder })
], STInvoiceMeta.prototype, "companyContact", void 0);
__decorate([
    field({ decoder: Address })
], STInvoiceMeta.prototype, "companyAddress", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], STInvoiceMeta.prototype, "companyVATNumber", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, optional: true })
], STInvoiceMeta.prototype, "companyNumber", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 133 })
], STInvoiceMeta.prototype, "ipAddress", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 133 })
], STInvoiceMeta.prototype, "userAgent", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 210 })
], STInvoiceMeta.prototype, "stripeAccountId", void 0);
export class STInvoice extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.payment = null;
        /**
         * If the number is null, no invoice is generated yet. Its still a WIP invoice (not an official one!)
         */
        this.number = null;
        this.createdAt = null;
        this.updatedAt = null;
        this.paidAt = null;
        this.negativeInvoiceId = null;
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], STInvoice.prototype, "id", void 0);
__decorate([
    field({ decoder: Payment, nullable: true })
], STInvoice.prototype, "payment", void 0);
__decorate([
    field({ decoder: STInvoiceMeta })
], STInvoice.prototype, "meta", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true })
], STInvoice.prototype, "number", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], STInvoice.prototype, "createdAt", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], STInvoice.prototype, "updatedAt", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], STInvoice.prototype, "paidAt", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 245 })
], STInvoice.prototype, "negativeInvoiceId", void 0);
export class STInvoicePrivate extends STInvoice {
    constructor() {
        super(...arguments);
        this.settlement = null;
        this.reference = null;
    }
    matchQuery(query) {
        var _a;
        if (query === ((_a = this.number) === null || _a === void 0 ? void 0 : _a.toString()) || query === this.id) {
            return true;
        }
        if (StringCompare.contains(this.meta.companyName, query) ||
            StringCompare.typoCount(this.meta.companyAddress.city, query) < 2 ||
            StringCompare.contains(this.meta.companyContact, query) ||
            (this.meta.companyVATNumber && StringCompare.typoCount(this.meta.companyVATNumber, query) < 2) ||
            StringCompare.typoCount(this.meta.companyAddress.street, query) < 2) {
            return true;
        }
        if (!this.organization) {
            return false;
        }
        if (StringCompare.typoCount(this.organization.name, query) < 2 ||
            StringCompare.typoCount(this.organization.address.city, query) < 2 ||
            StringCompare.typoCount(this.organization.address.street, query) < 2 ||
            StringCompare.typoCount(this.meta.companyName, query) < 2 ||
            StringCompare.typoCount(this.meta.companyName, query) < 2) {
            return true;
        }
        return false;
    }
}
__decorate([
    field({ decoder: OrganizationSimple, optional: true })
], STInvoicePrivate.prototype, "organization", void 0);
__decorate([
    field({ decoder: Settlement, nullable: true })
], STInvoicePrivate.prototype, "settlement", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 186 })
], STInvoicePrivate.prototype, "reference", void 0);
export class STPendingInvoice extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.id = null;
        this.invoice = null;
        this.createdAt = null;
        this.updatedAt = null;
    }
}
__decorate([
    field({ decoder: StringDecoder, nullable: true, optional: true })
], STPendingInvoice.prototype, "id", void 0);
__decorate([
    field({ decoder: STInvoiceMeta })
], STPendingInvoice.prototype, "meta", void 0);
__decorate([
    field({ decoder: STInvoice, nullable: true })
], STPendingInvoice.prototype, "invoice", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], STPendingInvoice.prototype, "createdAt", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], STPendingInvoice.prototype, "updatedAt", void 0);
export class STPendingInvoicePrivate extends STPendingInvoice {
}
__decorate([
    field({ decoder: OrganizationSimple, optional: true })
], STPendingInvoicePrivate.prototype, "organization", void 0);
export class STInvoiceResponse extends AutoEncoder {
}
__decorate([
    field({ decoder: StringDecoder, optional: true })
], STInvoiceResponse.prototype, "paymentUrl", void 0);
__decorate([
    field({ decoder: STInvoice, optional: true })
], STInvoiceResponse.prototype, "invoice", void 0);
//# sourceMappingURL=STInvoice.js.map